/**
 * WhatsAppLeadModal
 * Modal global que captura nome + telefone antes de abrir o WhatsApp.
 * Uso: chame openWhatsApp({ message, channel, origin }) de qualquer lugar.
 */
import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { getConversionContext } from "@/lib/conversionTracking";
import type { WhatsappFunnelEventType } from "../../../shared/whatsappFunnelTracking";

// ── Tipos ────────────────────────────────────────────────────────────────────

export interface WhatsAppLeadOptions {
  /** Mensagem pré-preenchida no WhatsApp (sem nome/telefone — serão anexados). */
  message: string;
  /** Canal de destino: número do WhatsApp a usar. */
  channel?: "comercial" | "suporte";
  /** Página/contexto de origem para rastreamento. */
  origin?: string;
}

// ── Singleton state (fora do React para ser acessível globalmente) ────────────

type ModalState = {
  open: boolean;
  options: WhatsAppLeadOptions | null;
};

let _setState: ((s: ModalState) => void) | null = null;
let _trackFunnelEvent: ((eventType: WhatsappFunnelEventType) => void) | null = null;

/** Abre o modal de captura de lead antes do WhatsApp. */
export function openWhatsApp(opts: WhatsAppLeadOptions) {
  _trackFunnelEvent?.("whatsapp_cta_click");
  if (_setState) {
    _setState({ open: true, options: opts });
    _trackFunnelEvent?.("whatsapp_modal_open");
  } else {
    // Fallback: abre direto se o modal não estiver montado
    const url = buildWhatsAppUrl(opts.channel ?? "comercial", opts.message, "", "");
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const WA_NUMBERS: Record<string, string> = {
  comercial: "5548991583576",
  suporte: "5548991339135",
};

function buildWhatsAppUrl(channel: string, baseMsg: string, name: string, phone: string) {
  const namePart = name.trim() ? ` Meu nome é ${name.trim()}` : "";
  const phonePart = phone.trim() ? `, meu telefone é ${phone.trim()}` : "";
  const full = baseMsg + (namePart ? `.${namePart}${phonePart}.` : "");
  const num = WA_NUMBERS[channel] ?? WA_NUMBERS.comercial;
  return `https://wa.me/${num}?text=${encodeURIComponent(full)}`;
}

// ── Componente ────────────────────────────────────────────────────────────────

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 32 32" fill="currentColor" className="h-5 w-5" aria-hidden="true">
      <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.58-1.67a12.74 12.74 0 0 0 6.22 1.6h.01c7.06 0 12.79-5.74 12.79-12.8s-5.74-12.73-12.8-12.73zm0 23.39h-.01a10.6 10.6 0 0 1-5.41-1.48l-.39-.23-4.02 1.02 1.07-3.86-.25-.4a10.55 10.55 0 0 1-1.63-5.64c0-5.86 4.78-10.63 10.65-10.63 2.84 0 5.51 1.11 7.52 3.11a10.56 10.56 0 0 1 3.11 7.53c0 5.86-4.78 10.58-10.64 10.58zm5.83-7.97c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.18.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.14 3.09 1.3 3.3.16.21 2.25 3.44 5.45 4.82.76.33 1.36.53 1.82.67.77.24 1.46.21 2.01.13.61-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
    </svg>
  );
}

export function WhatsAppLeadModal() {
  const [state, setState] = useState<ModalState>({ open: false, options: null });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);

  function maskPhone(value: string) {
    // Remove tudo que não for dígito
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length === 0) return "";
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    // 11 dígitos: celular (XX) XXXXX-XXXX
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  // Registra o setter global ao montar
  const setterRef = useCallback((node: null) => {
    if (node === null) {
      _setState = setState as unknown as (s: ModalState) => void;
    }
  }, []);
  // Registra imediatamente
  if (_setState !== setState) {
    _setState = setState as unknown as (s: ModalState) => void;
  }

  const capture = trpc.leads.captureWhatsapp.useMutation();
  const trackFunnel = trpc.leads.trackWhatsappFunnel.useMutation();

  const recordFunnelEvent = useCallback((eventType: WhatsappFunnelEventType) => {
    const { sessionId, utmSource, utmMedium, utmCampaign } = getConversionContext();
    trackFunnel.mutate({
      eventType,
      pagePath: window.location.pathname,
      sessionId,
      ...(utmSource ? { utmSource } : {}),
      ...(utmMedium ? { utmMedium } : {}),
      ...(utmCampaign ? { utmCampaign } : {}),
    });
  }, [trackFunnel]);
  _trackFunnelEvent = recordFunnelEvent;

  // Telefone válido: (XX) XXXXX-XXXX (15 chars) ou (XX) XXXX-XXXX (14 chars)
  const phoneDigits = phone.replace(/\D/g, "");
  const isPhoneValid = phoneDigits.length >= 10;
  const canSubmit = isPhoneValid && !loading;

  function handleClose(reason: "cancel" | "submit" = "cancel") {
    if (state.open && reason === "cancel") recordFunnelEvent("whatsapp_modal_cancel");
    setState({ open: false, options: null });
    setName("");
    setPhone("");
    setLoading(false);
    setStarted(false);
  }

  function handleStart() {
    if (started) return;
    setStarted(true);
    recordFunnelEvent("whatsapp_modal_start");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!state.options || !canSubmit) return;

    const { message, channel = "comercial", origin } = state.options;
    const context = getConversionContext();
    recordFunnelEvent("whatsapp_modal_submit");

    // Abre o WhatsApp IMEDIATAMENTE (dentro do handler do usuário)
    // para evitar que o browser bloqueie o popup
    const url = buildWhatsAppUrl(channel, message, name, phone);
    window.open(url, "_blank", "noopener,noreferrer");

    // Fecha o modal antes do await para não travar a UI
    handleClose("submit");

    // Salva o lead em background (não bloqueia nem exibe erro ao usuário)
    capture.mutate({
      name: name.trim() || "Não informado",
      phone: phone.trim() || "Não informado",
      channel,
      origin: origin ?? window.location.pathname,
      ...context,
    });
  }

  const channelLabel = state.options?.channel === "suporte" ? "Assistência Técnica" : "Comercial";

  return (
    <Dialog open={state.open} onOpenChange={(open) => !open && handleClose("cancel")}>
      <DialogContent className="mx-auto max-w-sm rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-charcoal">
            Fale com a gente
          </DialogTitle>
          <p className="mt-1 text-sm text-charcoal/55">
            Informe seu WhatsApp e abriremos o atendimento {channelLabel} imediatamente.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
          <Input
            placeholder="Seu nome"
            value={name}
            onFocus={handleStart}
            onChange={(e) => { handleStart(); setName(e.target.value); }}
            autoFocus
            className="rounded-xl border-charcoal/20 bg-softgray/40 text-sm"
          />
          <Input
            placeholder="(48) 99999-9999"
            type="tel"
            inputMode="numeric"
            value={phone}
            onFocus={handleStart}
            onChange={(e) => { handleStart(); setPhone(maskPhone(e.target.value)); }}
            maxLength={15}
            className="rounded-xl border-charcoal/20 bg-softgray/40 text-sm"
          />
          {/* Nome é opcional para reduzir o atrito antes da abertura do WhatsApp. */}
          {phone.length > 0 && !isPhoneValid && (
            <p className="-mt-1 text-xs text-red-500">Digite um telefone válido com DDD.</p>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] text-white font-semibold hover:bg-[#1ebe5d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            size="lg"
          >
            <WhatsAppIcon />
            {loading ? "Abrindo..." : "Conversar no WhatsApp"}
          </Button>
          <button
            type="button"
            onClick={() => handleClose("cancel")}
            className="text-center text-xs text-charcoal/40 hover:text-charcoal/60 transition-colors"
          >
            Cancelar
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
